/**
 * Config Slice
 *
 * Manages onboarding configuration state
 */
import { createSlice } from '@reduxjs/toolkit';
import type { OnboardingConfig } from '@contracts';
import { fetchOnboardingConfigThunk } from '../thunks/configThunks';

// ============================================
// State Types
// ============================================

export interface ConfigState {
  config: OnboardingConfig | null;
  isLoading: boolean;
  error: string | null;
}

// ============================================
// Initial State
// ============================================

export const initialConfigState: ConfigState = {
  config: null,
  isLoading: false,
  error: null,
};

// ============================================
// Slice
// ============================================

const configSlice = createSlice({
  name: 'onboarding/config',
  initialState: initialConfigState,
  reducers: {
    resetConfigState: () => initialConfigState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOnboardingConfigThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOnboardingConfigThunk.fulfilled, (state, action) => {
        state.config = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchOnboardingConfigThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

// ============================================
// Actions
// ============================================

export const { resetConfigState } = configSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const configReducer = configSlice.reducer;
